import React, { useState } from 'react';
import { observer } from 'mobx-react-lite';
import { Alert } from 'src/domains/common/components/alert/alert';
import { Button } from 'src/domains/common/components/button/button';
import Loading from 'src/components/Loading/Loading';
import { useAppStateContext } from 'src/AppState/AppState';
import { ConfigState } from 'src/AppState/ConfigState/ConfigState';
import { GamstopPreviouslyPositiveCustomLimitConfiguration } from 'src/components/ComplianceConfiguration/GamstopPreviouslyPositive/GamstopPreviouslyPositiveCustomLimitConfiguration';
import {
    GamstopPreviouslyPositiveComplianceRestrictionPeriodPanel,
    GamstopPreviouslyPositiveDepositRestrictionPeriodPanel,
} from 'src/components/ComplianceConfiguration/GamstopPreviouslyPositive/GamstopPreviouslyPositiveRestrictionPeriodPanel';
import {
    GamstopPreviouslyPositiveActions,
    GamstopPreviouslyPositiveDescription,
    GamstopPreviouslyPositiveWrapper,
} from 'src/components/ComplianceConfiguration/GamstopPreviouslyPositive/GamstopPreviouslyPositive.style';
import { GamstopPreviouslyPositiveActionSection } from 'src/components/ComplianceConfiguration/GamstopPreviouslyPositive/GamstopPreviouslyPositiveActionSection';
import { GamstopPreviouslyPositiveCommunicationChannelPanel } from 'src/components/ComplianceConfiguration/GamstopPreviouslyPositive/GamstopPreviouslyPositiveCommunicationChannelPanel';
import { GamstopPreviouslyPositiveCommunicationOnlyPanel } from 'src/components/ComplianceConfiguration/GamstopPreviouslyPositive/GamstopPreviouslyPositiveCommunicationOnlyPanel';
import { GamstopPreviouslyPositiveState } from 'src/components/ComplianceConfiguration/GamstopPreviouslyPositive/GamstopPreviouslyPositive.state';
import { GamstopPreviouslyPositiveTestTriggerPanel } from 'src/components/ComplianceConfiguration/GamstopPreviouslyPositive/GamstopPreviouslyPositiveTestTriggerPanel';
import {
    getGamstopPpVisibleActions,
    isPrimaryGamstopPpAction,
} from 'src/components/ComplianceConfiguration/GamstopPreviouslyPositive/GamstopPreviouslyPositive.types';

export const GamstopPreviouslyPositive = observer((): JSX.Element => {
    const appState = useAppStateContext();
    const configState = ConfigState.get(appState.common);
    const [state] = useState(() => new GamstopPreviouslyPositiveState(appState, configState));

    if (state.isLoading) {
        return (
            <GamstopPreviouslyPositiveWrapper data-test='gamstop-previously-positive-loading'>
                <Loading active />
            </GamstopPreviouslyPositiveWrapper>
        );
    }

    return (
        <GamstopPreviouslyPositiveWrapper data-test='gamstop-previously-positive'>
            <Alert
                icon='info'
                title='Automated action for Previously Positive users'
                variant='default'
            >
                Select one automated action to apply when a user returns as Previously Positive (PP). Primary actions
                can optionally include email or popup communication.
            </Alert>

            <GamstopPreviouslyPositiveDescription>
                Choose the action that should be applied automatically when a Gamstop check returns PP.
            </GamstopPreviouslyPositiveDescription>

            {state.errorMessage === null ? null : (
                <Alert
                    icon='info'
                    title={state.errorMessage}
                    variant='destructive'
                />
            )}

            <GamstopPreviouslyPositiveActionSection
                onSelect={state.setSelectedAction}
                options={getGamstopPpVisibleActions(configState.config.gamstopPpBlockedSuspendedActions)}
                renderAfterOption={(option): JSX.Element | null => {
                    if (option.value === 'apply-custom-limit' && state.isCustomLimitSelected) {
                        return (
                            <>
                                <GamstopPreviouslyPositiveCustomLimitConfiguration
                                    configState={configState}
                                    state={state}
                                />
                                <GamstopPreviouslyPositiveCommunicationChannelPanel
                                    isVisible
                                    state={state}
                                />
                            </>
                        );
                    }

                    if (option.value === 'compliance-restriction' && state.isComplianceRestrictionSelected) {
                        return (
                            <>
                                <GamstopPreviouslyPositiveComplianceRestrictionPeriodPanel state={state} />
                                <GamstopPreviouslyPositiveCommunicationChannelPanel
                                    isVisible
                                    state={state}
                                />
                            </>
                        );
                    }

                    if (option.value === 'deposit-restriction' && state.isDepositRestrictionSelected) {
                        return (
                            <>
                                <GamstopPreviouslyPositiveDepositRestrictionPeriodPanel state={state} />
                                <GamstopPreviouslyPositiveCommunicationChannelPanel
                                    isVisible
                                    state={state}
                                />
                            </>
                        );
                    }

                    if (isPrimaryGamstopPpAction(option.value)) {
                        return (
                            <GamstopPreviouslyPositiveCommunicationChannelPanel
                                isVisible={state.selectedAction === option.value}
                                state={state}
                            />
                        );
                    }

                    if (option.value === 'send-communication-only') {
                        return (
                            <GamstopPreviouslyPositiveCommunicationOnlyPanel
                                isVisible={state.isCommunicationOnlySelected}
                                state={state}
                            />
                        );
                    }

                    return null;
                }}
                selectedAction={state.selectedAction}
                title='Actions'
            />

            <GamstopPreviouslyPositiveActions>
                <Button
                    dataTest='gamstop-pp-save-configuration'
                    disabled={state.isSaving || !state.hasSelectedAction}
                    isLoading={state.isSaving}
                    onClick={(): void => {
                        void state.saveConfiguration();
                    }}
                    type='button'
                >
                    Save configuration
                </Button>
            </GamstopPreviouslyPositiveActions>

            <GamstopPreviouslyPositiveTestTriggerPanel state={state} />
        </GamstopPreviouslyPositiveWrapper>
    );
});
